import React from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { VisibilityOutlined as VisibilityIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { RootState } from "../../services/redux/store";

type FormCompletionSection = {
  sectionKey?: string | null;
  completionPercentage?: number | string | null;
  isCompleted?: boolean | null;
  horizontalStepId?: number | string | null;
  verticalStepId?: number | string | null;
  horizontalStepName?: string | null;
  verticalStepName?: string | null;
  stepName?: string | null;
  stepLabel?: string | null;
  label?: string | null;
  name?: string | null;
};

type CoApplicantCompletionSummary = {
  total: number;
  completed: number;
  completionPercentage: number;
};

type ComputedCompletionSummary = {
  coApplicant: CoApplicantCompletionSummary;
  horizontalSteps: Record<string, number>;
};

const REQUIRED_CO_APPLICANT_SECTIONS = ["basic", "address", "employment"];
const CO_APPLICANT_SECTION_COUNT = REQUIRED_CO_APPLICANT_SECTIONS.length;
const ADDITIONAL_DETAILS_INCLUDED_VERTICAL_STEP_IDS = [10, 12, 13, 14];
const HORIZONTAL_STEP_ID_BY_TITLE: Record<string, number> = {
  "eligibility check": 1,
  "applicant loan details": 2,
  "applicant & loan details": 2,
  "additional details documents": 4,
  "additional details & documents": 4,
};
const completionSummaryCache = new Map<string, Promise<unknown>>();

const getCompletionSummaryResponse = (
  trackingId: string,
  refreshNonce = 0
) => {
  const cacheKey = `${trackingId}:${refreshNonce}`;
  const cachedRequest = completionSummaryCache.get(cacheKey);
  if (cachedRequest) return cachedRequest;

  const request = eligibilityCheckServices
    .getFormCompletionSummary(trackingId)
    .then((response) => response?.data?.data ?? response?.data ?? null)
    .catch((error) => {
      completionSummaryCache.delete(cacheKey);
      throw error;
    });

  completionSummaryCache.set(cacheKey, request);
  return request;
};

const toNumericId = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toCompletionNumber = (value: unknown) => {
  const numeric = Number(String(value ?? "0").replace("%", ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const isCompletionFlagTrue = (value: unknown) =>
  value === true || String(value ?? "").toLowerCase() === "true";

const isCompletionSectionComplete = (section: FormCompletionSection) =>
  isCompletionFlagTrue(section.isCompleted) ||
  toCompletionNumber(section.completionPercentage) >= 100;

const getCoApplicantNumberFromText = (value: unknown) => {
  const text = String(value ?? "");
  const match =
    text.match(/^(?:basic|address|employment)CoApplicant(\d+)/i) ||
    text.match(/^coApplicant(\d+)_(?:basic|address|employment)$/i) ||
    text.match(/^coapplicant(\d+)$/i);
  const numeric = match ? Number(match[1]) : 0;
  return Number.isFinite(numeric) ? numeric : 0;
};

const collectCoApplicantNumbers = (
  source: unknown,
  seen = new WeakSet<object>()
): number[] => {
  if (!source) return [];

  if (typeof source === "string" || typeof source === "number") {
    const number = getCoApplicantNumberFromText(source);
    return number > 0 ? [number] : [];
  }

  if (Array.isArray(source)) {
    return source.flatMap((item) => collectCoApplicantNumbers(item, seen));
  }

  if (typeof source !== "object") return [];
  if (seen.has(source)) return [];
  seen.add(source);

  return Object.entries(source as Record<string, any>).flatMap(
    ([key, value]) => {
      const keyNumber = getCoApplicantNumberFromText(key);
      const valueNumbers = collectCoApplicantNumbers(value, seen);
      return keyNumber > 0 ? [keyNumber, ...valueNumbers] : valueNumbers;
    }
  );
};

const normalizeCompletionTitle = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const getHorizontalStepName = (record: Record<string, any>) =>
  record.horizontalStepName ??
  record.horizontal_step_name ??
  record.stepName ??
  record.step_name ??
  record.stepLabel ??
  record.step_label ??
  record.label ??
  record.name;

const getVerticalStepName = (record: Record<string, any>) =>
  record.verticalStepName ??
  record.vertical_step_name ??
  record.stepName ??
  record.step_name ??
  record.stepLabel ??
  record.step_label ??
  record.label ??
  record.name;

const collectCompletionSections = (
  source: unknown,
  inheritedIds: Pick<
    FormCompletionSection,
    "horizontalStepId" | "verticalStepId" | "horizontalStepName" | "verticalStepName"
  > = {}
): FormCompletionSection[] => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.flatMap((item) =>
      collectCompletionSections(item, inheritedIds)
    );
  }
  if (typeof source !== "object") return [];

  const record = source as Record<string, any>;
  const horizontalStepName = getHorizontalStepName(record);
  const verticalStepName = getVerticalStepName(record);
  const currentIds = {
    horizontalStepId:
      record.horizontalStepId ??
      record.horizontal_step_id ??
      record.horizontalId ??
      inheritedIds.horizontalStepId,
    verticalStepId:
      record.verticalStepId ??
      record.vertical_step_id ??
      record.verticalId ??
      inheritedIds.verticalStepId,
    horizontalStepName:
      horizontalStepName ?? inheritedIds.horizontalStepName,
    verticalStepName:
      verticalStepName ?? inheritedIds.verticalStepName,
  };
  const current: FormCompletionSection[] =
    typeof record.sectionKey === "string"
      ? [
          {
            sectionKey: record.sectionKey,
            completionPercentage: record.completionPercentage,
            isCompleted: record.isCompleted,
            ...currentIds,
          },
        ]
      : [];

  const nested = Object.values(record).flatMap((value) =>
    collectCompletionSections(value, currentIds)
  );
  return current.concat(nested);
};

const collectHorizontalCompletionSections = (
  source: unknown
): FormCompletionSection[] => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.flatMap((item) => collectHorizontalCompletionSections(item));
  }
  if (typeof source !== "object") return [];

  const record = source as Record<string, any>;
  const horizontalStepName = getHorizontalStepName(record);
  const horizontalStepId =
    record.horizontalStepId ?? record.horizontal_step_id ?? record.horizontalId;
  const verticalStepId =
    record.verticalStepId ?? record.vertical_step_id ?? record.verticalId;
  const hasHorizontalIdentity =
    horizontalStepName !== undefined || horizontalStepId !== undefined;
  const hasVerticalIdentity =
    verticalStepId !== undefined && verticalStepId !== null;
  const hasCompletion = record.completionPercentage !== undefined;
  const isSectionCompletion = typeof record.sectionKey === "string";

  const current: FormCompletionSection[] =
    hasHorizontalIdentity &&
    hasCompletion &&
    !hasVerticalIdentity &&
    !isSectionCompletion
      ? [
          {
            completionPercentage: record.completionPercentage,
            horizontalStepId,
            horizontalStepName,
            stepName: record.stepName ?? record.step_name,
            stepLabel: record.stepLabel ?? record.step_label,
            label: record.label,
            name: record.name,
          },
        ]
      : [];

  return current.concat(
    Object.values(record).flatMap((value) =>
      collectHorizontalCompletionSections(value)
    )
  );
};

const collectVerticalCompletionSections = (
  source: unknown,
  inheritedHorizontal: Pick<
    FormCompletionSection,
    "horizontalStepId" | "horizontalStepName"
  > = {}
): FormCompletionSection[] => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.flatMap((item) =>
      collectVerticalCompletionSections(item, inheritedHorizontal)
    );
  }
  if (typeof source !== "object") return [];

  const record = source as Record<string, any>;
  const horizontalStepName = getHorizontalStepName(record);
  const horizontalStepId =
    record.horizontalStepId ??
    record.horizontal_step_id ??
    record.horizontalId ??
    inheritedHorizontal.horizontalStepId;
  const currentHorizontal = {
    horizontalStepId,
    horizontalStepName:
      horizontalStepName ?? inheritedHorizontal.horizontalStepName,
  };
  const verticalStepId =
    record.verticalStepId ?? record.vertical_step_id ?? record.verticalId;
  const verticalStepName = getVerticalStepName(record);
  const hasVerticalIdentity =
    verticalStepId !== undefined ||
    record.verticalStepName !== undefined ||
    record.vertical_step_name !== undefined;
  const hasCompletion = record.completionPercentage !== undefined;
  const isSectionCompletion = typeof record.sectionKey === "string";

  const current: FormCompletionSection[] =
    hasVerticalIdentity && hasCompletion && !isSectionCompletion
      ? [
          {
            completionPercentage: record.completionPercentage,
            isCompleted: record.isCompleted,
            verticalStepId,
            verticalStepName,
            ...currentHorizontal,
          },
        ]
      : [];

  return current.concat(
    Object.values(record).flatMap((value) =>
      collectVerticalCompletionSections(value, currentHorizontal)
    )
  );
};

const normalizeHorizontalCompletion = (
  horizontalStepId: number | null,
  stepName: unknown,
  completion: number
) => {
  const normalizedStepName = normalizeCompletionTitle(stepName);
  if (
    (horizontalStepId === 2 ||
      normalizedStepName === "applicant loan details" ||
      normalizedStepName === "applicant & loan details") &&
    Math.abs(completion - 83.33) < 0.01
  ) {
    return 100;
  }

  return completion;
};

const calculateAdditionalDetailsCompletionFromVerticalSteps = (
  source: unknown
) => {
  const verticalSteps = collectVerticalCompletionSections(source).filter(
    (section) => {
      const horizontalStepId = toNumericId(section.horizontalStepId);
      const horizontalStepName = normalizeCompletionTitle(
        section.horizontalStepName
      );
      return (
        horizontalStepId === 4 ||
        horizontalStepName === "additional details documents" ||
        horizontalStepName === "additional details & documents"
      );
    }
  );

  const byVerticalId = new Map<number, number>();
  verticalSteps.forEach((section) => {
    const verticalStepId = toNumericId(section.verticalStepId);
    if (
      verticalStepId === null ||
      !ADDITIONAL_DETAILS_INCLUDED_VERTICAL_STEP_IDS.includes(verticalStepId)
    ) {
      return;
    }

    const rawCompletion = isCompletionFlagTrue(section.isCompleted)
      ? 100
      : toCompletionNumber(section.completionPercentage);
    const completion = rawCompletion >= 100 ? 100 : 0;
    const current = byVerticalId.get(verticalStepId) ?? 0;
    byVerticalId.set(
      verticalStepId,
      Math.max(current, Math.max(0, Math.min(100, completion)))
    );
  });

  if (byVerticalId.size === 0) return undefined;

  const total = ADDITIONAL_DETAILS_INCLUDED_VERTICAL_STEP_IDS.reduce(
    (sum, verticalStepId) => sum + (byVerticalId.get(verticalStepId) ?? 0),
    0
  );
  return Number(
    (total / ADDITIONAL_DETAILS_INCLUDED_VERTICAL_STEP_IDS.length).toFixed(2)
  );
};

export const calculateCoApplicantCompletionSummary = (
  source: unknown,
  coApplicantCountSources: unknown[] = []
): CoApplicantCompletionSummary => {
  const groups = new Map<number, Map<string, number>>();

  collectCompletionSections(source).forEach((section) => {
    const match = String(section.sectionKey || "").match(
      /^coApplicant(\d+)_(basic|address|employment)$/i
    );
    if (!match) return;
    const horizontalStepId = toNumericId(section.horizontalStepId);
    const verticalStepId = toNumericId(section.verticalStepId);
    const verticalStepName = normalizeCompletionTitle(section.verticalStepName);
    const isLoanDetailsCoApplicantSection =
      horizontalStepId === 2 && verticalStepId === 9;
    const isAdditionalCoApplicantSection =
      horizontalStepId === 4 &&
      verticalStepId === 11 &&
      verticalStepName.includes("co applicant");
    const hasNoStepContext = horizontalStepId === null && verticalStepId === null;
    if (
      !hasNoStepContext &&
      !isLoanDetailsCoApplicantSection &&
      !isAdditionalCoApplicantSection
    ) {
      return;
    }

    const coApplicantNumber = Number(match[1]);
    const sectionName = match[2].toLowerCase();
    if (!Number.isFinite(coApplicantNumber)) return;
    if (!groups.has(coApplicantNumber)) {
      groups.set(coApplicantNumber, new Map<string, number>());
    }

    const sectionCompletion = isCompletionFlagTrue(section.isCompleted)
      ? 100
      : toCompletionNumber(section.completionPercentage);
    const existingValue = groups.get(coApplicantNumber)?.get(sectionName) ?? 0;
    groups.get(coApplicantNumber)?.set(
      sectionName,
      Math.max(existingValue, Math.max(0, Math.min(100, sectionCompletion)))
    );
  });

  const fallbackCoApplicantNumbers = coApplicantCountSources.flatMap((item) =>
    collectCoApplicantNumbers(item)
  );
  const fallbackCoApplicantCount = new Set(
    fallbackCoApplicantNumbers.filter((number) => number > 0)
  ).size;
  const actualCoApplicantCount =
    groups.size > 0
      ? Math.max(groups.size, fallbackCoApplicantCount)
      : fallbackCoApplicantCount;
  const effectiveCoApplicantCount =
    actualCoApplicantCount === 1 ? 2 : Math.max(actualCoApplicantCount, 2);
  const sectionWeight = 100 / CO_APPLICANT_SECTION_COUNT;
  const overallCompletion = Array.from(groups.values()).reduce(
    (totalContribution, sections) =>
      totalContribution +
      REQUIRED_CO_APPLICANT_SECTIONS.reduce((sectionContribution, sectionName) => {
        const completion = Math.max(
          0,
          Math.min(100, sections.get(sectionName) ?? 0)
        );
        return (
          sectionContribution +
          (completion / 100) * (sectionWeight / effectiveCoApplicantCount)
        );
      }, 0),
    0
  );
  const completed = Array.from(groups.values()).filter((sections) =>
    REQUIRED_CO_APPLICANT_SECTIONS.every(
      (sectionName) => (sections.get(sectionName) ?? 0) >= 100
    )
  ).length;

  return {
    total: actualCoApplicantCount,
    completed,
    completionPercentage: Number(overallCompletion.toFixed(2)),
  };
};

export const calculateHorizontalCompletionSummary = (
  source: unknown
): Record<string, number> => {
  const byTitle: Record<string, number> = {};

  collectHorizontalCompletionSections(source).forEach((section) => {
    const stepName =
      section.horizontalStepName ??
      section.stepName ??
      section.stepLabel ??
      section.label ??
      section.name;
    const normalizedStepName = normalizeCompletionTitle(stepName);
    const horizontalStepId = toNumericId(section.horizontalStepId);
    const completion = normalizeHorizontalCompletion(
      horizontalStepId,
      stepName,
      toCompletionNumber(section.completionPercentage)
    );

    if (normalizedStepName) {
      byTitle[normalizedStepName] = completion;
    }
    if (horizontalStepId !== null) {
      byTitle[`id:${horizontalStepId}`] = completion;
    }
  });

  const additionalDetailsCompletion =
    calculateAdditionalDetailsCompletionFromVerticalSteps(source);
  if (additionalDetailsCompletion !== undefined) {
    byTitle["additional details documents"] = additionalDetailsCompletion;
    byTitle["id:4"] = additionalDetailsCompletion;
  }

  return byTitle;
};

const getApiCompletionForTitle = (
  title: string,
  horizontalSteps: Record<string, number>
) => {
  const normalizedTitle = normalizeCompletionTitle(title);
  const titleMatch = horizontalSteps[normalizedTitle];
  if (titleMatch !== undefined) return titleMatch;

  const horizontalStepId = HORIZONTAL_STEP_ID_BY_TITLE[normalizedTitle];
  if (horizontalStepId !== undefined) {
    return horizontalSteps[`id:${horizontalStepId}`];
  }

  return undefined;
};

// --- Props Interface ---
export interface CollapsibleSectionProps {
  icon?: React.ElementType<any>;
  title: string;
  subtitle?: string;
  completionRate?: string;
  isCompleted?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isProvisionalOfferTab?: boolean;
  provisionalOfferBtnClick?: () => void;
  paddingTopTabZero?: boolean;
  isProvisionalOffer?: any;
  partnerStartedJourney?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  icon: Icon,
  title,
  subtitle,
  completionRate,
  isExpanded,
  isCompleted,
  onToggle,
  children,
  isProvisionalOfferTab,
  provisionalOfferBtnClick,
  paddingTopTabZero,
  isProvisionalOffer,
  partnerStartedJourney,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const trackingId = useSelector((state: RootState) => state.stepper.trackingId);
  const completionSummaryRefreshNonce = useSelector(
    (state: RootState) => state.stepper.completionSummaryRefreshNonce
  );
  const formData = useSelector((state: RootState) => state.stepper.formData);
  const horizontalSteps = useSelector(
    (state: RootState) => state.stepper.horizontalSteps
  );
  const displayHorizontalSteps = useSelector(
    (state: RootState) => state.stepper.displayHorizontalSteps
  );
  const fetchUserApplicationData = useSelector(
    (state: RootState) => state.stepper.fetchUserApplicationData
  );
  const isPartnerStartedJourney = Boolean(
    partnerStartedJourney ?? fetchUserApplicationData?.partnerStartedJourney
  );
  const [completionSummaryData, setCompletionSummaryData] =
    React.useState<unknown>(null);
  const computedCompletionSummary = React.useMemo<ComputedCompletionSummary>(
    () => ({
      coApplicant: {
        ...calculateCoApplicantCompletionSummary(completionSummaryData, [
          formData,
          horizontalSteps,
          displayHorizontalSteps,
          fetchUserApplicationData,
        ]),
      },
      horizontalSteps: calculateHorizontalCompletionSummary(completionSummaryData),
    }),
    [
      completionSummaryData,
      formData,
      horizontalSteps,
      displayHorizontalSteps,
      fetchUserApplicationData,
    ]
  );

  React.useEffect(() => {
    let isMounted = true;
    const currentTrackingId = String(trackingId || "").trim();

    setCompletionSummaryData(null);
    if (!currentTrackingId || isPartnerStartedJourney) return;

    getCompletionSummaryResponse(
      currentTrackingId,
      completionSummaryRefreshNonce
    )
      .then((summaryData) => {
        if (!isMounted) return;
        setCompletionSummaryData(summaryData);
      })
      .catch((error) => {
        console.error("Failed to fetch form completion summary", error);
      });

    return () => {
      isMounted = false;
    };
  }, [trackingId, completionSummaryRefreshNonce, isPartnerStartedJourney]);

  const effectiveCompletionRate =
    title === "Co-Applicant"
      ? `${computedCompletionSummary.coApplicant.completionPercentage}%`
      : `${
          getApiCompletionForTitle(
            title,
            computedCompletionSummary.horizontalSteps
          ) ?? toCompletionNumber(completionRate)
        }%`;
  const completionValue = Number(String(effectiveCompletionRate || "0").replace("%", ""));
  const normalizedCompletion = Number.isFinite(completionValue)
    ? Math.max(0, Math.min(100, completionValue))
    : 0;
  return (
    <Paper
      sx={{
        p: isXsOrSm ? 1 : 2.5,
        mb: 2,
        borderRadius: '8px',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          pb: isExpanded ? 2 : 0,
          borderBottom: isExpanded ? '1px solid #eee' : 'none',
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {Icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '6px',
                bgcolor: '#e0f7fa',
              }}
            >
              <Icon
                sx={{
                  fontSize: '1.5rem',
                  color: '#d3ebebff',
                }}
              />
            </Box>
          )}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
              {title}
            </Typography>
            {!isProvisionalOfferTab && !isPartnerStartedJourney && effectiveCompletionRate && (
              <Box
                sx={{
                  mt: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: isXsOrSm ? 'auto' : '340px',
                }}
              >
                {title.startsWith('Reference') ||
                title.startsWith('Collateral') ? null : (
                  <LinearProgress
                    variant="determinate"
                    value={normalizedCompletion}
                    sx={{
                      width: isXsOrSm ? '120px' : '170px',
                      height: isXsOrSm ? '7px' : '8px',
                      borderRadius: '999px',
                      bgcolor: '#e0f2f4',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#00b3b5',
                        borderRadius: '999px',
                      },
                    }}
                  />
                )}

                {title.startsWith('Reference') ||
                title.startsWith('Collateral') ? null : (
                  <Typography
                    sx={{
                      fontSize: isXsOrSm ? '11px' : '12px',
                      fontWeight: 500,
                      color: '#a4a1a1ff',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Completion Rate
                    <Typography
                      component="span"
                      sx={{
                        fontSize: isXsOrSm ? '11px' : '12px',
                        fontWeight: 600,
                        color: '#8f8b99',
                        ml: 1,
                      }}
                    >
                      {effectiveCompletionRate}
                    </Typography>
                  </Typography>
                )}
              </Box>
            )}
            {/* {subtitle && (
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  color: "#a4a1a1ff",
                }}
              >
                {subtitle}
              </Typography>
            )} */}
          </Box>
        </Box>

        {isXsOrSm && (
          <Box>
            {isProvisionalOfferTab ? (
              <Button
                variant="outlined"
                onClick={provisionalOfferBtnClick}
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  border: 'none',
                  color: isCompleted ? '#00b3b5' : '#616161',
                }}
                startIcon={<VisibilityIcon />}
                disabled={!isProvisionalOffer}
              >
                View
              </Button>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {/* {!partnerStartedJourney && completionRate && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: isCompleted ? "#00b3b5" : "#616161",
                      ml: 0.5,
                    }}
                  >
                    {completionRate}
                  </Typography>
                )} */}
                <IconButton size="small" sx={{ color: '#616161' }}>
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {!isXsOrSm && (
          <Box>
            {isProvisionalOfferTab ? (
              <Button
                variant="outlined"
                onClick={provisionalOfferBtnClick}
                sx={{
                  mt: isXsOrSm ? 2 : 0,
                  border: 'none',
                  textTransform: 'capitalize',
                  color: '#00b3b5',
                  // "&:hover": {
                  //   bgcolor: "white",
                  //   border: "none",
                  // },
                }}
                disabled={!isProvisionalOffer}
                startIcon={<VisibilityIcon />}
              >
                View Provisional Offer
              </Button>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isXsOrSm ? 'space-between' : 'normal',
                  width: isXsOrSm ? '100%' : 'auto',
                  gap: 1.5,
                  mt: isXsOrSm ? 2 : 0,
                }}
              >
                {/* {!partnerStartedJourney && completionRate && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#a4a1a1ff",
                        fontSize: "0.7rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Completion Rate{" "}
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 600,
                          color: isCompleted ? "#00b3b5" : "#616161",
                          ml: 1,
                        }}
                      >
                        {completionRate}
                      </Typography>
                    </Typography>

                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <CircularProgress
                        variant="determinate"
                        value={Number(completionRate?.replace("%", "") || "0")}
                        size={28}
                        thickness={4}
                        sx={{
                          color: "#00b3b5",
                          position: "absolute",
                          left: 0,
                          top: 0,
                          zIndex: 1,
                        }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={28}
                        thickness={4}
                        sx={{
                          color: "#e0e0e0",
                        }}
                      />
                    </Box>
                  </Box>
                )} */}
                <IconButton size="small" sx={{ color: '#616161' }}>
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ pt: paddingTopTabZero ? 0 : 2 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleSection;
